import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Loading, Page, PageHeader, Unavailable, Waiting } from '@beyond-js/ui/react';
import { config } from '@snapshots/config';
import { Links } from '@snapshots/domain';
import { Address, Copies, Origin, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { Outages, Problems, type Missed } from '@snapshots/ui';
import { Empty, Quota, Room } from './empty';
import { SnapshotList, type Heard } from './list';

/**
 * The snapshots a person may see, as the family's list (SNP-09, D162, `SnapshotList`): of every organization
 * of theirs, of one organization (`?organization=`), or of one project (`?project=`, where Projects' and
 * Packages' Snapshots entries lead), filtered by state and searched (title, package, identity or digest) by
 * the service, newest first and grouped by when they were made, 20 to a page. The scope, the state, the words
 * and the page live in the address.
 *
 * Nothing is made here: snapshots are made in Packages, which the header offers inside a project and every
 * empty list leads to (`Empty`). A person in no organization, a project or organization they cannot read, a
 * list with nothing yet and a search with no matches are four different things, each said as such.
 *
 * Every state is one family `Page` (D52) of the list template at the fluid width: the arrival line when
 * another product opened it, then its one header, "Snapshots", with where the list is as its lead: a sentence,
 * so never in the facts line (D191).
 *
 * A list nobody answered is said once (D163): one line on the H1's edge with who did not answer, when it last
 * tried and one Try again, and "Not available right now" where the table would be. The quota's line is read
 * beside the list, so its read joins the same line while the list is unanswered. With nothing else to show,
 * the line is the page's body.
 *
 * The quota's line is known only once its own read answers, so it comes after the list: arriving there, it
 * moves nothing already drawn (D185). It is read once for the list's organization, above the list, so
 * choosing another state draws the list again and leaves the line as it was.
 */
export function Overview() {
	const [parameters] = useSearchParams();
	const address = new Address(parameters);
	const { text, identity } = usePresence();
	const { descriptor } = useFrame();
	useEffect(() => {
		document.title = text('page.list');
	}, [text]);
	// The organization the list is in, when it is in one: the project's (as the bar names it), the one asked, or the only one
	const organizations = identity?.organizations ?? [];
	const described = descriptor && !('unavailable' in descriptor) ? descriptor : undefined;
	const home = address.organization ?? (address.project ? described?.organization?.id : undefined) ?? (organizations.length === 1 ? organizations[0].organization : undefined);
	const quota = Room.use(home);
	// A new scope is a new list; its search, state and page are the list's own, kept in the address
	return <Listing key={address.scope} address={address} home={home} quota={quota} />;
}

function Listing({ address, home, quota }: { address: Address; home?: string; quota: ReturnType<typeof Room.use> }) {
	const { text, identity, language } = usePresence();
	const { descriptor, names, arrival } = useFrame();
	const [heard, setHeard] = useState<Heard>({ nothing: false });
	const hear = useCallback((part: Partial<Heard>) => setHeard(current => ({ ...current, ...part })), []);
	// Where the list's search and filter stand: the header's action region, before its action (L8)
	const [outlet, setOutlet] = useState<HTMLSpanElement | null>(null);
	const owner = Copies.unavailable(language);
	const organizations = identity?.organizations ?? [];
	const { project, organization } = address;
	const links = new Links(descriptor, { packages: config.packages });
	const described = descriptor && !('unavailable' in descriptor) ? descriptor : undefined;

	// The page every state of the list is drawn in, with what its header says under the title
	const page = (children: ReactNode, lead?: ReactNode, actions?: ReactNode) => (
		<Page template="list" width="fluid" arrival={<Origin project={project} />} header={<PageHeader title={text('captures.title')} headingRef={arrival.heading} lead={lead} actions={actions} />}>
			{children}
		</Page>
	);

	if (!organizations.length) {
		// The way on comes from the bar's descriptor, so the pattern waits for it in place under the header
		// (it always settles, as unavailable at worst) rather than appearing first without one (LOAD-01)
		if (descriptor === null) return page(<Waiting label={Loading.text({ name: text('loading.organizations'), kind: 'reading', language })} language={language} kind="lines" />);
		const accounts = links.entry('accounts');
		const projectsHome = described?.links?.home || config.projects;
		const projects = projectsHome ? <Button variant={accounts?.url ? 'secondary' : 'primary'} href={projectsHome} label={text('unavailable.member.projects')} /> : null;
		return page(
			<div data-scope="none">
				<Unavailable
					kind="association"
					title={text('unavailable.member.title')}
					reason={text('unavailable.member.reason')}
					owner={text('unavailable.member.owner')}
					labels={owner}
					action={accounts?.url && accounts.available ? <Button variant="primary" href={accounts.url} label={text('unavailable.member.action')} /> : projects}
					secondary={accounts?.url && accounts.available ? projects : null}
				/>
			</div>
		);
	}
	const { missing } = heard;
	if (missing) {
		const kind = project ? 'project' : 'organization';
		return page(
			<div data-missing={kind}>
				<Unavailable kind="access" title={text(`unavailable.${kind}.title`)} reason={text(`unavailable.${kind}.reason`)} owner={text(`unavailable.${kind}.owner`)} labels={owner} details={Problems.details(missing)} action={<Button variant="primary" href="/" label={text('unavailable.all')} />} />
			</div>
		);
	}

	const named = project ? names.name(project) : organization ? names.name(organization) : undefined;
	// An organization whose name has not answered is never said by its identifier (D134): its sentence waits for the name, as Storage's does
	const where = project ? text('captures.scope.project', { project: named ?? text('nav.project.unknown') }) : organization ? (named ? text('captures.scope.organization', { organization: named }) : undefined) : text('captures.scope.all');
	const make = project && home ? links.packages({ organization: home, project }) : undefined;
	const lead = (
		<span data-where>
			{where}
			{(project || organization) && (
				<>
					{where && ' · '}
					<Link to="/" className="text-ink underline">
						{text('unavailable.all')}
					</Link>
				</>
			)}
		</span>
	);

	// The list, and while it is unanswered the quota's read too, are one line; the page is that line when nothing else shows
	const outage = heard.outage;
	const reads: Missed[] = outage ? [outage, ...(quota.outage ? [{ key: 'storage', title: text('outage.read.storage'), refusal: quota.outage, run: quota.read }] : [])] : [];
	const whole = Boolean(outage) && !Room.of(quota.storage);
	const actions = (
		<>
			<span ref={setOutlet} data-list-tools hidden={whole} />
			{make && links.enablement.state !== 'off' && <Button href={make} label={text('captures.make')} />}
		</>
	);

	return page(
		<>
			{/* The one line at the column's start, in one place whether it is the page's body or a line above the list */}
			<Outages reads={reads} whole={whole} text={text} language={language} />
			<div className="flex flex-col gap-4" data-scope={address.scope} hidden={whole}>
				<SnapshotList address={address} outlet={outlet} hidden={heard.nothing} onHeard={hear} />
				{/* The first run, from the same read as the list's structure, in the list's place under its tools (D185, D194) */}
				{heard.nothing && <Empty project={project} organization={home} />}
				{/* Known once its own read answers: after the list, its arrival moves nothing already drawn (D185) */}
				<Quota storage={quota.storage} />
			</div>
		</>,
		lead,
		actions
	);
}
